import React from "react";
import { motion } from "framer-motion";
import { Building2, ChevronRight, Eye, EyeOff, Layers, Users, Wallet } from "lucide-react";
import { AI_TIERS } from "@/constants/aiTiersCatalog";
import { MagneticButton, RevealOnScroll, scrollToId } from "../../marketingAgency/shared";
import {
  WHITE_LABEL_CTA,
  WHITE_LABEL_DETAILS,
  WHITE_LABEL_HERO_METRIC,
  WHITE_LABEL_PILLARS,
  WHITE_LABEL_SPLIT,
  WHITE_LABEL_THESIS,
} from "./whiteLabelWeaponContent";

const PILLAR_ICONS = {
  brand: Layers,
  book: Users,
  margin: Wallet,
};

const tierLayerLabel = (tier) => {
  if (tier.id === 1) return "Tier 1: Starter (Local contractors)";
  if (tier.id === 2) return "Tier 2: Growth (Multi-location hubs)";
  return "Tier 3: Scale (Franchise networks)";
};

const PillarCard = ({ pillar, index }) => {
  const Icon = PILLAR_ICONS[pillar.id] || Building2;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.35, delay: index * 0.08 }}
      className="rounded-xl border border-[#00D2FF]/25 bg-[#00D2FF]/5 p-5 sm:p-6"
      data-testid={`white-label-pillar-${pillar.id}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg border border-[#00D2FF]/35 bg-[#00D2FF]/10 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-partner-accent" />
        </div>
        <p className="text-[10px] font-mono text-partner-accent tracking-widest uppercase">{pillar.label}</p>
      </div>
      <p className="text-base font-bold text-partner mb-1">{pillar.headline}</p>
      <p className="text-sm text-partner-muted font-body leading-relaxed">{pillar.sub}</p>
    </motion.div>
  );
};

const SplitColumn = ({ side, variant, delay = 0 }) => {
  const isVisible = variant === "visible";
  const Icon = isVisible ? Eye : EyeOff;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35, delay }}
      className={`rounded-xl border p-5 sm:p-6 ${
        isVisible ? "border-[#00D2FF]/30 bg-[#00D2FF]/5" : "border-partner bg-[#11151F]"
      }`}
      data-testid={`white-label-split-${variant}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-4 h-4 ${isVisible ? "text-partner-accent" : "text-partner-muted"}`} />
        <p className="text-[10px] font-mono uppercase tracking-wider text-partner-muted">{side.label}</p>
      </div>
      <ul className="space-y-2">
        {side.items.map((item) => (
          <li key={item} className="text-sm font-body text-partner-muted flex gap-2">
            <span className="text-partner-accent shrink-0">·</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const MarginHero = () => (
  <div
    className="rounded-xl border border-partner bg-[#11151F] px-5 sm:px-8 py-8 sm:py-10 text-center"
    data-testid="white-label-hero-metric"
  >
    <motion.p
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="font-mono text-5xl sm:text-6xl font-bold text-partner-accent tabular-nums mb-2"
    >
      {WHITE_LABEL_HERO_METRIC.value}
    </motion.p>
    <p className="text-sm sm:text-base font-semibold text-partner mb-2">{WHITE_LABEL_HERO_METRIC.label}</p>
    <p className="text-sm text-partner-muted font-body">{WHITE_LABEL_HERO_METRIC.caption}</p>
  </div>
);

const TierTable = () => (
  <>
    <div className="hidden md:block overflow-x-auto rounded-xl border border-partner">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="border-b border-partner bg-[#11151F]">
            {["Infrastructure Layer", "Your Wholesale Cost", "Recommended Retail", "Your Monthly Margin"].map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-[10px] font-mono tracking-wider text-partner-muted uppercase"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {AI_TIERS.map((tier) => (
            <tr key={tier.id} className="border-b border-partner last:border-b-0">
              <td className="px-4 py-4 text-sm font-semibold text-partner">{tierLayerLabel(tier)}</td>
              <td className="px-4 py-4 text-sm font-mono text-partner">${tier.wholesale} / mo</td>
              <td className="px-4 py-4 text-sm font-mono text-partner-muted">
                ${tier.retailMin} – ${tier.retailMax} / mo
              </td>
              <td className="px-4 py-4 text-sm font-mono text-partner-accent">{tier.profitLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="md:hidden space-y-4">
      {AI_TIERS.map((tier) => (
        <div key={tier.id} className="rounded-xl border border-partner bg-[#11151F] p-5">
          <p className="text-sm font-semibold text-partner mb-3">{tierLayerLabel(tier)}</p>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-partner-muted">Wholesale</dt>
              <dd className="font-mono text-partner">${tier.wholesale}/mo</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-partner-muted">Retail</dt>
              <dd className="font-mono text-partner-muted">
                ${tier.retailMin}–${tier.retailMax}/mo
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-partner-muted">Margin</dt>
              <dd className="font-mono text-partner-accent">{tier.profitLabel}</dd>
            </div>
          </dl>
        </div>
      ))}
    </div>
  </>
);

const WhiteLabelWeaponSection = ({ id = "white-label-weapon" }) => (
  <section
    id={id}
    className="section-padding section-alt relative overflow-hidden"
    data-testid="white-label-weapon"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <RevealOnScroll>
        <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight mb-4 max-w-2xl">
          {WHITE_LABEL_THESIS.headline}
        </h2>
        <p className="text-lg sm:text-xl font-body max-w-2xl leading-relaxed mb-8">
          <span className="text-partner-muted">{WHITE_LABEL_THESIS.thesisLead}</span>
          <span className="gradient-text font-semibold">{WHITE_LABEL_THESIS.thesisAccent}</span>
        </p>
      </RevealOnScroll>

      <RevealOnScroll delay={0.06}>
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {WHITE_LABEL_PILLARS.map((pillar, index) => (
            <PillarCard key={pillar.id} pillar={pillar} index={index} />
          ))}
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.1}>
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-8" data-testid="white-label-split-visual">
          <SplitColumn side={WHITE_LABEL_SPLIT.hidden} variant="hidden" delay={0} />
          <SplitColumn side={WHITE_LABEL_SPLIT.visible} variant="visible" delay={0.08} />
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.12}>
        <MarginHero />
      </RevealOnScroll>

      <RevealOnScroll delay={0.14}>
        <details className="group rounded-xl border border-partner bg-[#11151F] mt-8 mb-10">
          <summary className="cursor-pointer list-none px-5 py-4 text-sm font-semibold text-partner flex items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
            {WHITE_LABEL_DETAILS.summary}
            <ChevronRight className="w-4 h-4 text-partner-muted transition-transform group-open:rotate-90 shrink-0" />
          </summary>
          <div className="px-5 pb-5 border-t border-partner space-y-4 pt-4">
            {WHITE_LABEL_DETAILS.paragraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 32)} className="text-sm text-partner-muted font-body leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </details>
      </RevealOnScroll>

      <RevealOnScroll delay={0.16}>
        <TierTable />
      </RevealOnScroll>

      <RevealOnScroll delay={0.2}>
        <div className="mt-10 text-center">
          <MagneticButton
            className="px-8 py-3.5 rounded-2xl font-semibold text-base"
            onClick={() => scrollToId("partner-form")}
            data-testid="white-label-claim-cta"
          >
            {WHITE_LABEL_CTA}
          </MagneticButton>
        </div>
      </RevealOnScroll>
    </div>
  </section>
);

export default WhiteLabelWeaponSection;
